import axios from "axios";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL.replace(/\/$/, "")}/api`;
const isAppRequest = config => typeof config?.url === "string" && config.url.startsWith(`${API}/`);

// Session lives in an httpOnly cookie set by the backend; nothing is kept in web storage.
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const generation = useRef(0);
  const currentUser = useRef(null);
  const pendingCheck = useRef(null);
  const setSession = useCallback(value => { currentUser.current = value; setUser(value); }, []);

  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(config => {
      if (isAppRequest(config)) config.sessionGeneration = generation.current;
      return config;
    });
    const responseInterceptor = axios.interceptors.response.use(
      response => response,
      error => {
        const config = error?.config;
        const ticket = config?.sessionGeneration;
        if (error?.response?.status === 401 && isAppRequest(config) && !config.skipSessionCheck
            && ticket === generation.current && currentUser.current) {
          const invalidate = () => {
            if (ticket === generation.current) { generation.current += 1; setSession(null); }
          };
          if (config.url === `${API}/auth/me`) invalidate();
          else if (!pendingCheck.current || pendingCheck.current.ticket !== ticket) {
            // A form-level401 (for example a wrong current password) is not proof
            // that the signed-in session expired. Confirm once per generation.
            const check = { ticket };
            pendingCheck.current = check;
            axios.get(`${API}/auth/me`, { skipSessionCheck: true })
              .catch(e => { if (e?.response?.status === 401) invalidate(); })
              .finally(() => { if (pendingCheck.current === check) pendingCheck.current = null; });
          }
        }
        return Promise.reject(error);
      }
    );
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [setSession]);

  useEffect(() => {
    const controller = new AbortController();
    const ticket = generation.current;
    axios.get(`${API}/auth/me`, { signal: controller.signal, skipSessionCheck: true })
      .then(r => { if (ticket === generation.current && !controller.signal.aborted) setSession(r.data); })
      .catch(() => { if (ticket === generation.current && !controller.signal.aborted) setSession(null); })
      .finally(() => { if (!controller.signal.aborted) setReady(true); });
    return () => controller.abort();
  }, [setSession]);

  const login = useCallback(async (identifier, password) => {
    const ticket = ++generation.current;
    await axios.post(`${API}/auth/login`, { identifier, password }, { skipSessionCheck: true });
    try {
      const { data } = await axios.get(`${API}/auth/me`, { skipSessionCheck: true });
      if (ticket === generation.current) setSession(data);
    } catch (error) {
      if (ticket === generation.current) setSession(null);
      if (error?.response?.status === 401) throw new Error("Giriş bilgileri doğrulandı ancak oturum korunamadı. Lütfen yöneticinize bildirin.");
      throw error;
    }
  }, [setSession]);

  const logout = useCallback(async () => {
    generation.current += 1;
    await axios.post(`${API}/auth/logout`, {}, { skipSessionCheck: true });
    setSession(null);
  }, [setSession]);

  const refreshUser = useCallback(async () => {
    const ticket = generation.current;
    try { const { data } = await axios.get(`${API}/auth/me`); if (ticket === generation.current) setSession(data); }
    catch { /* The session interceptor handles confirmed expiry. */ }
  }, [setSession]);

  const can = useCallback(
    permission => !!user && (user.role === "admin" || (user.permissions || []).includes(permission)),
    [user]
  );

  const value = useMemo(
    () => ({ user, ready, login, logout, refreshUser, can, isAdmin: user?.role === "admin" }),
    [user, ready, login, logout, refreshUser, can]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
